import { useAudio } from '../contexts/AudioContext'

interface Props {
  onClose: () => void
}

export function QueueDrawer({ onClose }: Props) {
  const {
    currentItem,
    queue,
    removeFromQueue,
    clearQueue
  } = useAudio()

  const formatDuration = (seconds: number): string => {
    const mins = Math.ceil(seconds / 60)
    return `${mins} min`
  }

  return (
    <div className="queue-drawer-overlay" onClick={onClose}>
      <div className="queue-drawer" onClick={e => e.stopPropagation()}>
        <div className="drawer-header">
          <h3>Up Next</h3>
          <button onClick={onClose} aria-label="Close">
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {currentItem && (
          <div className="now-playing">
            <span className="now-playing-label">Now Playing</span>
            <div className="now-playing-track">
              <span className="track-title">{currentItem.title}</span>
              <span className="track-source">{currentItem.source}</span>
            </div>
            <div className="now-playing-icon">
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" className="playing-animation">
                <rect x="4" y="4" width="3" height="16" rx="1">
                  <animate attributeName="height" values="16;8;16" dur="0.8s" repeatCount="indefinite" />
                  <animate attributeName="y" values="4;8;4" dur="0.8s" repeatCount="indefinite" />
                </rect>
                <rect x="10.5" y="4" width="3" height="16" rx="1">
                  <animate attributeName="height" values="16;12;16" dur="0.6s" repeatCount="indefinite" />
                  <animate attributeName="y" values="4;6;4" dur="0.6s" repeatCount="indefinite" />
                </rect>
                <rect x="17" y="4" width="3" height="16" rx="1">
                  <animate attributeName="height" values="16;10;16" dur="0.7s" repeatCount="indefinite" />
                  <animate attributeName="y" values="4;7;4" dur="0.7s" repeatCount="indefinite" />
                </rect>
              </svg>
            </div>
          </div>
        )}

        {queue.length === 0 ? (
          <div className="empty-queue">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M9 18V5l12-2v13" />
              <circle cx="6" cy="18" r="3" />
              <circle cx="18" cy="16" r="3" />
            </svg>
            <p>Queue is empty</p>
            <p className="hint">Add articles to listen</p>
          </div>
        ) : (
          <>
            <div className="queue-header">
              <span>{queue.length} article{queue.length !== 1 ? 's' : ''}</span>
              <button onClick={clearQueue} className="clear-btn">Clear all</button>
            </div>

            <ul className="queue-list">
              {queue.map((item, index) => (
                <li key={item.id} className="queue-item">
                  <div className="drag-handle" title="Drag to reorder">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor">
                      <path d="M8 6h2v2H8V6zm6 0h2v2h-2V6zM8 11h2v2H8v-2zm6 0h2v2h-2v-2zM8 16h2v2H8v-2zm6 0h2v2h-2v-2z"/>
                    </svg>
                  </div>
                  <div className="item-number">{index + 1}</div>
                  <div className="item-info">
                    <span className="item-title">{item.title}</span>
                    <span className="item-meta">
                      {item.source} • {formatDuration(item.estimatedDuration)}
                    </span>
                  </div>
                  <button
                    className="remove-btn"
                    onClick={() => removeFromQueue(item.id)}
                    aria-label="Remove from queue"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M18 6L6 18M6 6l12 12" />
                    </svg>
                  </button>
                </li>
              ))}
            </ul>
          </>
        )}
      </div>
    </div>
  )
}
